import React from 'react';
import { DatePicker, Spin } from 'antd';
import dayjs from 'dayjs';
import {
  CalculatorIcon,
  CalendarDaysIcon,
  ClockIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import {
  TWButton,
  TWSelect,
  TWDateRangePicker,
  FieldLabel,
} from '../../../components/ui';
import { formatCurrency } from '../../../utils/baseUtils';

const { RangePicker } = DatePicker;

export default function TipCalculator({
  selectedTipRange,
  storeList,
  calculateTip,
  loading,
  disabled,
  onChange,
  onCalculate,
}) {
  const canCalculate =
    !loading && selectedTipRange.storeids?.length > 0 && !disabled;

  return (
    <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
      <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm transition dark:border-slate-700 dark:bg-slate-900/60">
        <div className="space-y-5">
          <TWDateRangePicker
            label="Dates"
            labelIcon={CalendarDaysIcon}
            value={[
              dayjs(selectedTipRange.startdate, 'YYYY-MM-DD'),
              dayjs(selectedTipRange.enddate, 'YYYY-MM-DD'),
            ]}
            onChange={(e) => onChange(e, 'date')}
            disabledDate={(current, { from } = {}) => {
              if (from) {
                return Math.abs(current.diff(from, 'day')) >= 14;
              }
              return false;
            }}
          />

          <div>
            <FieldLabel icon={ClockIcon}>Shift hours</FieldLabel>
            <RangePicker
              className="tw-picker"
              style={{ width: '100%' }}
              size="large"
              picker="time"
              format="hh:mm A"
              value={[
                dayjs(selectedTipRange.startTime, 'HH:mm'),
                dayjs(selectedTipRange.endTime, 'HH:mm'),
              ]}
              onChange={(e) => onChange(e, 'time')}
            />
          </div>

          <TWSelect
            label="Store"
            labelIcon={BuildingStorefrontIcon}
            disabled={disabled}
            placeholder="Select a store"
            onChange={(e) => onChange(e, 'store')}
            options={storeList.map((a) => ({
              label: a.name,
              value: a.id,
            }))}
            value={selectedTipRange.storeids}
          />

          <TWButton
            variant="primary"
            block
            icon={CalculatorIcon}
            label={loading ? 'Calculating…' : 'Calculate tips'}
            disabled={!canCalculate}
            onClick={onCalculate}
          />
        </div>
      </div>

      <div className="relative overflow-hidden rounded-md border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-sky-50 p-6 shadow-sm dark:border-sky-900/40 dark:from-sky-950/30 dark:via-slate-900/60 dark:to-slate-900/40">
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-blue-500/10 blur-3xl dark:bg-sky-400/10" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-600 dark:text-sky-300">
            Summary
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Spin size="large" />
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Total orders
                </p>
                <p className="mt-1 text-3xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                  {calculateTip.orderCount || 0}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Total tip
                </p>
                <p className="mt-1 text-3xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(calculateTip.totalTip)}
                </p>
              </div>
            </div>
          )}

          <p className="mt-6 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
            Date range must be less than 14 days. Results reflect tips on paid
            orders during the selected shift window.
          </p>
        </div>
      </div>
    </div>
  );
}
